import { createApi } from "@reduxjs/toolkit/query/react";

import apiConfig from "configs/apiConfig";
import { REDUCER_PATHS } from "store/config";
import { transformResponseHandler } from "utils/transformResponseHandler";

import { customFetchBase } from ".";

const masterApi = createApi({
  reducerPath: REDUCER_PATHS.MASTER,
  baseQuery: customFetchBase,
  endpoints: (builder) => ({
    getTimeZone: builder.query({
      query: () => ({
        url: apiConfig.TIMEZONE,
        method: "GET",
      }),
      providesTags: ["Timezone"],
    }),

    timezoneList: builder.mutation({
      query: (data) => ({
        url: apiConfig.TIMEZONE_LIST,
        method: "POST",
        body: data,
      }),
    }),
    timezoneDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.TIMEZONE,
        method: "DELETE",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addTimezone: builder.mutation({
      query: (data) => ({
        url: apiConfig.TIMEZONE,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Timezone"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    getCountry: builder.query({
      query: () => ({
        url: apiConfig.COUNTRY,
        method: "GET",
      }),
      providesTags: ["Country"],
    }),
    timezoneChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.TIMEZONE,
        body: data,
        method: "PATCH",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    // country
    countryList: builder.query({
      query: (data) => ({
        url: `${apiConfig.COUNTRY}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["countryListing"],
    }),
    countryListDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.COUNTRY,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["countryListing"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    countryListChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.COUNTRY,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["Currency", "countryListing"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addCountryList: builder.mutation({
      query: (data) => ({
        url: apiConfig.COUNTRY,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Currency", "Country", "countryListing"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    currencyList: builder.query({
      query: () => ({
        url: apiConfig.CURRENCY_LIST,
        method: "GET",
      }),
      providesTags: ["Currency"],
    }),
    // Language
    getLanguage: builder.query({
      query: () => ({
        url: apiConfig.GET_LANGUAGE,
        method: "GET",
      }),
      providesTags: ["Language"],
    }),

    languageList: builder.query({
      query: (data) => ({
        url: `${apiConfig.LANGUAGE}?page=${data.page}&pageSize=${data.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["languageListing"],
    }),
    languageListChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.LANGUAGE,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["Language", "languageListing"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    languageListDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.LANGUAGE,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["Language", "languageListing"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addLanguageList: builder.mutation({
      query: (data) => ({
        url: apiConfig.LANGUAGE,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Language", "languageListing"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),

    //City
    cityListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.CITY_LISTING,
        method: "POST",
        body: data,
      }),
    }),
    addCity: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_CITY,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    changeCity: builder.mutation({
      query: (data) => ({
        url: apiConfig.CHANGE_CITY,
        body: data,
        method: "PATCH",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
      invalidatesTags: ["city"],
    }),
    cityDropdownList: builder.query({
      query: () => ({
        url: apiConfig.GET_CITY_DROPDOWN,
        method: "GET",
      }),
    }),
    deleteCity: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_CITY,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editCityDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_CITY + "/" + data?.cityId,
        body: {
          city: data.city,
          status: data?.status,
          latitude: data?.latitude,
          longitude: data?.longitude,
          cityDisplayName: data?.cityDisplayName,
          ianaTimeZoneID: data?.ianaTimeZoneID,
          timeZoneName: data?.timeZoneName,
          rank: data?.rank,
          clubTypeIds: data?.clubTypeIds,
          musicTypeIds: data?.musicTypeIds,
          planIds: data?.planIds,
        },
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    //club Type
    clubTypeListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.CLUB_TYPE_LIST,
        method: "POST",
        body: data,
      }),
    }),
    addClubType: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_CLUB_TYPE,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    changeClubType: builder.mutation({
      query: (data) => ({
        url: apiConfig.CHANGE_CLUB_TYPE,
        body: data,
        method: "PATCH",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteClubType: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_CLUB_TYPE,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editClubTypeDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_CLUB_TYPE + "/" + data?.clubTypeId,
        body: { clubType: data.clubType, status: data.status },
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    getClubTypeDropdown: builder.mutation({
      query: () => ({
        url: apiConfig.DROPDOWN_CLUB_TYPE,
        method: "GET",
      }),
    }),
    //zones
    zonesListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.ZONES_LIST,
        method: "POST",
        body: data,
      }),
    }),
    addZones: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_ZONES,
        method: "POST",
        body: data,
      }),
      // invalidatesTags: ["Currency", "Country", "countryListing"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    changeZones: builder.mutation({
      query: (data) => ({
        url: apiConfig.CHANGE_ZONES,
        body: data,
        method: "PATCH",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteZones: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_ZONES,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editZonesDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_ZONES + "/" + data?.zoneId,
        body: { zone: data?.zone, cityId: data.cityId, status: data?.status },
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    zoneDropdownList: builder.mutation({
      query: (data) => ({
        url: apiConfig.DROPDOWN_ZONES + "/" + data?.cityId,
        method: "GET",
      }),
    }),
    //drink type
    drinkTypeListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.DRINK_TYPE,
        method: "POST",
        body: data,
      }),
    }),
    drinkTypeDropdown: builder.mutation({
      query: () => ({
        url: apiConfig.DRINK_TYPE_DROPDOWN,
        method: "GET",
      }),
    }),
    addDrinkType: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_DRINK_TYPE,
        method: "POST",
        body: data,
      }),
      // invalidatesTags: ["Currency", "Country", "countryListing"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    changeDrinkType: builder.mutation({
      query: (data) => ({
        url: apiConfig.CHANGE_DRINK_TYPE,
        body: data,
        method: "PATCH",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
      // invalidatesTags: ["roleDetails", "roleListing"],
    }),
    deleteDrinkType: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_DRINK_TYPE,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editDrinkTypeDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_DRINK_TYPE + "/" + data?.id,
        body: { drinkType: data.drinkType, status: data?.status },
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),

    //music type
    musicTypeListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.MUSIC_TYPE_LIST,
        method: "POST",
        body: data,
      }),
    }),
    addMusicType: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_MUSIC_TYPE,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    changeMusicType: builder.mutation({
      query: (data) => ({
        url: apiConfig.CHANGE_MUSIC_TYPE,
        body: data,
        method: "PATCH",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteMusicType: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_MUSIC_TYPE,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editMusicTypeDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_MUSIC_TYPE + "/" + data?.id,
        body: { musicType: data.musicType, status: data?.status },
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    getMusicTypeDropdown: builder.mutation({
      query: () => ({
        url: apiConfig.DROPDOWN_MUSIC_TYPE,
        method: "GET",
      }),
    }),
    // outfits
    outfitsListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.OUTFITS_LIST,
        method: "POST",
        body: data,
      }),
    }),
    addOutfits: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_OUTFITS,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    changeOutfits: builder.mutation({
      query: (data) => ({
        url: apiConfig.CHANGE_OUTFITS,
        body: data,
        method: "PATCH",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteOutfits: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_OUTFITS,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editOutfitsDetails: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_OUTFITS + "/" + data?.id,
        body: { outfit: data.outfit, status: data?.status },
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    getOutfitDropdown: builder.mutation({
      query: () => ({
        url: apiConfig.DROPDOWN_OUTFIT,
        method: "GET",
      }),
    }),
    // Plans
    plansListing: builder.mutation({
      query: (data) => ({
        url: apiConfig.PLANS_LIST,
        method: "POST",
        body: data,
      }),
    }),
    addPlans: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADD_PLANS,
        method: "POST",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    chnagePlan: builder.mutation({
      query: (data) => ({
        url: apiConfig.CHANGE_PLANS,
        body: data,
        method: "PATCH",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    pinPlan: builder.mutation({
      query: (data) => ({
        url: apiConfig.PIN_PLAN + "/" + data?.id,
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    unPinPlan: builder.mutation({
      query: (data) => ({
        url: apiConfig.UNPIN_PLAN + "/" + data?.id,
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deletePlan: builder.mutation({
      query: (data) => ({
        url: apiConfig.DELETE_PLANS,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editPlan: builder.mutation({
      query: (data) => ({
        url: apiConfig.EDIT_PLANS + "/" + data?.id,
        body: {
          title: data?.title,
          color: data?.color,
          backgroundImage: data?.backgroundImage,
          clubTypes: data?.clubTypes,
          outfits: data?.outfits,
          zones: data?.zones,
          musicTypes: data?.musicTypes,
          status: data?.status,
          addedClubs: data?.addedClubs,
          avgAgeMin: data?.avgAgeMin,
          avgAgeMax: data?.avgAgeMax,
          drinkPriceMin: data?.drinkPriceMin,
          drinkPriceMax: data?.drinkPriceMax,
        },
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    plansFileUplaod: builder.mutation({
      query: (data) => ({
        url: apiConfig.FILE_UPLOAD_PLANS,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    getPlanDropdown: builder.mutation({
      query: () => ({
        url: apiConfig.DROPDOWN_PLAN,
        method: "GET",
      }),
    }),
  }),
});

export const {
  usePlansFileUplaodMutation,
  useZoneDropdownListMutation,
  useGetTimeZoneQuery,
  useTimezoneListMutation,
  useTimezoneDeleteMutation,
  useAddTimezoneMutation,
  useGetCountryQuery,
  useTimezoneChangeStatusMutation,
  useLazyCountryListQuery,
  useCountryListDeleteMutation,
  useCountryListChangeStatusMutation,
  useAddCountryListMutation,
  useCurrencyListQuery,
  useGetLanguageQuery,
  useLazyLanguageListQuery,
  useLanguageListChangeStatusMutation,
  useLanguageListDeleteMutation,
  useAddLanguageListMutation,

  useAddCityMutation,
  useChangeCityMutation,
  useCityListingMutation,
  useLazyCityDropdownListQuery,
  useDeleteCityMutation,
  useEditCityDetailsMutation,

  useAddClubTypeMutation,
  useChangeClubTypeMutation,
  useDeleteClubTypeMutation,
  useEditClubTypeDetailsMutation,
  useClubTypeListingMutation,

  useAddZonesMutation,
  useChangeZonesMutation,
  useZonesListingMutation,
  useDeleteZonesMutation,
  useEditZonesDetailsMutation,

  useAddDrinkTypeMutation,
  useChangeDrinkTypeMutation,
  useDeleteDrinkTypeMutation,
  useEditDrinkTypeDetailsMutation,
  useDrinkTypeListingMutation,
  useDrinkTypeDropdownMutation,

  useAddMusicTypeMutation,
  useChangeMusicTypeMutation,
  useDeleteMusicTypeMutation,
  useEditMusicTypeDetailsMutation,
  useMusicTypeListingMutation,

  useAddOutfitsMutation,
  useChangeOutfitsMutation,
  useDeleteOutfitsMutation,
  useEditOutfitsDetailsMutation,
  useOutfitsListingMutation,

  useAddPlansMutation,
  usePlansListingMutation,
  useChnagePlanMutation,
  usePinPlanMutation,
  useUnPinPlanMutation,
  useDeletePlanMutation,
  useEditPlanMutation,
  useGetClubTypeDropdownMutation,
  useGetMusicTypeDropdownMutation,
  useGetOutfitDropdownMutation,
  useGetPlanDropdownMutation,
} = masterApi;

export default masterApi;
